import {
    type ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react';

import { useBlocker } from '@tanstack/react-router';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';

type ReportDirty = (id: string, isDirty: boolean) => void;

const UnsavedChangesContext = createContext<ReportDirty | null>(null);

/** Registers this editor's dirty state with the nearest guard (no-op outside one). */
export function useReportUnsavedChanges(isDirty: boolean) {
    const report = useContext(UnsavedChangesContext);
    const id = useId();

    useEffect(() => {
        report?.(id, isDirty);
        return () => report?.(id, false);
    }, [report, id, isDirty]);
}

/** Blocks in-app navigation and warns on reload/close while any child editor is dirty. */
export function UnsavedChangesGuard({
    children,
    stayIsPrimary = false,
}: {
    children: ReactNode;
    /** Makes Stay the highlighted, initially focused button instead of Leave. */
    stayIsPrimary?: boolean;
}) {
    const stayRef = useRef<HTMLButtonElement>(null);
    const [dirtyIds, setDirtyIds] = useState<ReadonlySet<string>>(new Set());
    const hasUnsavedRef = useRef(false);
    hasUnsavedRef.current = dirtyIds.size > 0;

    const report = useCallback<ReportDirty>((id, isDirty) => {
        setDirtyIds((prev) => {
            if (prev.has(id) === isDirty) return prev;
            const next = new Set(prev);
            if (isDirty) next.add(id);
            else next.delete(id);
            return next;
        });
    }, []);

    const blocker = useBlocker({
        // Tab switches only change `?tab=`, so they stay on the same pathname.
        shouldBlockFn: ({ current, next }) =>
            hasUnsavedRef.current && current.pathname !== next.pathname,
        enableBeforeUnload: () => hasUnsavedRef.current,
        withResolver: true,
    });

    return (
        <UnsavedChangesContext.Provider value={report}>
            {children}
            <AlertDialog
                open={blocker.status === 'blocked'}
                onOpenChange={(open) => {
                    if (!open && blocker.status === 'blocked') blocker.reset();
                }}
            >
                <AlertDialogContent
                    data-testid="UnsavedChangesGuard__dialog"
                    onOpenAutoFocus={
                        stayIsPrimary
                            ? (event) => {
                                  event.preventDefault();
                                  stayRef.current?.focus();
                              }
                            : undefined
                    }
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Leave without saving?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. If you leave this page
                            they will be lost.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            ref={stayRef}
                            className={
                                stayIsPrimary ? buttonVariants() : undefined
                            }
                        >
                            Stay
                        </AlertDialogCancel>
                        <AlertDialogAction
                            className={
                                stayIsPrimary
                                    ? `${buttonVariants({ variant: 'outline' })} text-foreground`
                                    : undefined
                            }
                            onClick={() => {
                                if (blocker.status === 'blocked')
                                    blocker.proceed();
                            }}
                        >
                            Leave
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </UnsavedChangesContext.Provider>
    );
}
